import type { ITourStep } from '@components/PageTour/types';
import { closeOnLeave } from '@components/PageTour/stepHelpers';
import { SchedulerStockFigure } from './SchedulerStockFigure';
import { DashboardAlertsFigure } from './DashboardAlertsFigure';

export const LOGISTICS_TOUR_ID = 'logistics';

/** What the script's steps read off the page. */
export interface ILogisticsTourContext {
  /** The action step's real control is on the page. It is not rendered for a
   *  caller who cannot write, and an empty delivery board renders a different
   *  CTA — either way the action step and the dialog steps skip. */
  hasNewDeliveryButton: boolean;
  /** Closes the order dialog the action step opened. */
  close: () => void;
}

/** `data-tour` on the order dialog's popup — the scope the two in-dialog
 *  steps look for their fields inside. */
const ORDER_DIALOG = 'logistics-order-dialog';

/**
 * The Logistics tour, top to bottom (spec §The Logistics tour). Copy lives in
 * `tours.logistics.<stepId>.*` in both locales; targets are `data-tour`
 * attributes on the page. The action step and the two steps inside the order
 * dialog are skipped wherever the header's `+ New delivery` button is not
 * rendered — the two dialog steps only exist once that button has opened the
 * form.
 */
export const LOGISTICS_TOUR_STEPS: ITourStep<ILogisticsTourContext>[] = [
  { id: 'welcome' },
  { id: 'kpis', target: 'logistics-kpis', side: 'bottom' },
  { id: 'search', target: 'logistics-search', side: 'left' },
  { id: 'pipeline', target: 'logistics-pipeline', side: 'inside', scroll: 'start' },
  {
    id: 'newDelivery',
    target: 'logistics-new-delivery',
    side: 'bottom',
    align: 'end',
    pad: 8,
    action: { target: 'logistics-new-delivery' },
    skipWhen: (ctx) => !ctx.hasNewDeliveryButton,
  },
  {
    id: 'orderLines',
    // Scoped to the dialog: the delivery drawer's Edit tab renders the same
    // form inline, so these field ids exist twice once that drawer is open.
    within: ORDER_DIALOG,
    target: ['logistics-order-materials', 'logistics-order-destination'],
    side: 'right',
    skipWhen: (ctx) => !ctx.hasNewDeliveryButton,
    onLeave: closeOnLeave('back', (ctx) => ctx.close()),
  },
  {
    id: 'orderDates',
    within: ORDER_DIALOG,
    target: ['logistics-order-dates', 'logistics-order-note'],
    side: 'right',
    skipWhen: (ctx) => !ctx.hasNewDeliveryButton,
    onLeave: closeOnLeave('forward', (ctx) => ctx.close()),
  },
  { id: 'inventory', target: 'logistics-inventory', side: 'right', align: 'start' },
  { id: 'waste', target: 'logistics-waste', side: 'left', align: 'start' },
  { id: 'siteMap', target: 'logistics-site-map', side: 'inside', scroll: 'start' },
  { id: 'fromScheduler', target: 'logistics-pipeline', side: 'inside', scroll: 'start', figure: SchedulerStockFigure },
  { id: 'fromDashboard', target: 'logistics-alerts', side: 'bottom', figure: DashboardAlertsFigure },
  { id: 'done' },
];

/** Which body lines a read-only caller reads instead: the default lines invite
 *  a drag, a rename, `+ Add stock` and "try one thing", none of which a viewer
 *  can do. */
const VIEWER_COPY: Record<string, ITourStep['copy']> = {
  welcome: { body2: 'body2Viewer' },
  pipeline: { body: 'bodyViewer', body2: 'body2Viewer' },
  inventory: { body2: 'body2Viewer' },
};

/** The same script for a caller who cannot write (the action step and the
 *  dialog steps already skip for them — see `hasNewDeliveryButton`). */
export const LOGISTICS_VIEWER_TOUR_STEPS: ITourStep<ILogisticsTourContext>[] = LOGISTICS_TOUR_STEPS.map((step) =>
  step.id in VIEWER_COPY ? { ...step, copy: VIEWER_COPY[step.id] } : step,
);
